const DAY_MS = 24 * 60 * 60 * 1000;

const startOfUtcDay = (time) => {
  const date = new Date(time);
  return Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
};

// Daily up/down time for the last `days` UTC days, today included up to `now`.
//
// statusChanges: [{ type: "deposit" | "withdrawal", status: "up" | "down", timestamp }]
// currentUp: the exchange's current state, used only when there are no
//   changes at all (the state has then been constant).
//
// Each change is a transition, so the state before the first change in the
// window is the opposite of that change (an "up" means it was down before),
// unless an earlier change before the window says otherwise. Without any
// changes the current state is used. Unknown time is reported separately and
// never counted as downtime.
export function computeDailyUptime(statusChanges, type, currentUp, { days = 30, now = new Date() } = {}) {
  const nowMs = now.getTime();
  const firstDay = startOfUtcDay(nowMs) - (days - 1) * DAY_MS;

  const events = (statusChanges || [])
    .filter((change) => change.type === type)
    .map((change) => ({
      time: new Date(change.timestamp).getTime(),
      up: change.status === "up",
    }))
    .filter((event) => !Number.isNaN(event.time) && event.time <= nowMs)
    .sort((a, b) => a.time - b.time);

  let state = null;
  const before = events.filter((event) => event.time < firstDay);
  const inWindow = events.filter((event) => event.time >= firstDay);
  if (before.length) {
    state = before[before.length - 1].up;
  } else if (inWindow.length) {
    state = !inWindow[0].up;
  } else if (typeof currentUp === "boolean") {
    state = currentUp;
  }

  const result = [];
  let totalUp = 0;
  let totalDown = 0;
  let eventIndex = 0;

  for (let day = 0; day < days; day++) {
    const dayStart = firstDay + day * DAY_MS;
    const dayEnd = Math.min(dayStart + DAY_MS, nowMs);
    const bucket = { date: new Date(dayStart), up: 0, down: 0, unknown: 0, changes: 0 };
    const add = (from, to) => {
      const duration = Math.max(0, to - from);
      if (state === null) bucket.unknown += duration;
      else if (state) bucket.up += duration;
      else bucket.down += duration;
    };

    let cursor = dayStart;
    while (eventIndex < inWindow.length && inWindow[eventIndex].time < dayEnd) {
      const event = inWindow[eventIndex];
      add(cursor, event.time);
      state = event.up;
      cursor = event.time;
      bucket.changes += 1;
      eventIndex += 1;
    }
    add(cursor, dayEnd);

    totalUp += bucket.up;
    totalDown += bucket.down;
    result.push(bucket);
  }

  const known = totalUp + totalDown;
  return {
    days: result,
    uptimePercent: known ? (totalUp / known) * 100 : null,
  };
}

// "up" | "down" | "partial" | "unknown" for one day bucket
export function dayState(bucket) {
  if (!bucket.up && !bucket.down) return "unknown";
  if (!bucket.down) return "up";
  if (!bucket.up) return "down";
  return "partial";
}

export function formatDuration(ms) {
  const totalMinutes = Math.round(ms / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (!hours) return `${minutes}m`;
  return minutes ? `${hours}h ${minutes}m` : `${hours}h`;
}
